(ns manage.ad-variant.creation.ad-builder.modal
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/Step$default" :as Step]
            ["@mui/material/StepLabel$default" :as StepLabel]
            ["@mui/material/Stepper$default" :as Stepper]
            ["src/svg/type/thumbnails/meta-single-image.svg$default" :as MetaSingleImage]
            [common.flags :refer [use-flag]]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.queries :refer [use-mutation use-query]]
            [common.resource.query.template :as template.q]
            [common.util :as util]
            [common.widgets.form-field :refer [form-field hint]]
            [manage.ad-variant.creation.ad-builder.card-group-item :as cgi]
            [manage.ad-variant.creation.ad-builder.hooks :as h]
            [manage.ad-variant.creation.ad-builder.queries :as q]
            [manage.ad-variant.creation.video-format-picker :as vfp]
            [manage.ad-variant.data :as d]
            [manage.ad-variant.queries :as aq]
            [manage.adset-template-page.view.templates-selection :as ts]
            [manage.adset.hooks :refer [use-adset use-channel]]
            [manage.template-table.db :refer [template-table-ns]]
            [re-frame.core :as rf]
            [uix.core :refer [$ defui] :as uix]
            [uix.re-frame :refer [use-subscribe]]))

(def step->view
  {0 :ad-type
   1 :template})

;; Video has no real ad-type choice (only "vast-linear"), so it gets a format
;; (OLV/STV) step instead, which decides the ad-builder-urn used at creation.
(def video-format-steps ["Select format" "Select template"])

(defui video-format-stepper [{:keys [active-step]}]
  ($ Stepper {:activeStep active-step :alternativeLabel true :sx #js {:pt 0.5}}
     (for [label video-format-steps]
       ($ Step {:key label}
          ($ StepLabel label)))))

(def default-card-opts
  {:thumbnail MetaSingleImage
   :title ""
   :description ""
   :value ""})

(defui ad-types-selector
  [{:keys [visible-ad-types ad-type set-ad-type]}]
  ($ Stack {:direction "row" :sx #js {:gap 2}}
     (for [[value cfg] visible-ad-types]
       ($ cgi/card-group-item
          (merge default-card-opts
                 cfg
                 {:key value
                  :value value
                  :selected-value ad-type
                  :on-click set-ad-type})))))

(defui template-select
  [{:keys [not-selectable channel select-fn]}]
  (let [ns (template-table-ns channel)
        qualify (uix/use-callback (fn [e]
                                    (keyword ns e))
                                  [ns])]
    (uix/use-effect (fn []
                      (rf/dispatch [(qualify :init)]))
                    [qualify])
    ($ :<>
       ($ ts/audit-alert
          {:channel channel})
       ($ ts/filter-input
          {:qualify qualify})
       ($ ts/templates-selection-table
          {:qualify qualify
           :channel channel
           :multiselect-enabled? false
           :select-fn select-fn
           :is-row-selectable (fn [^js params]
                                (let [template (.-row params)
                                      audit-state (-> template :latestRevision :auditState)
                                      template-dimensions ((juxt :width :height) (:latestRevision template))]
                                  ;; A template is selectable if..
                                  ;; its audit has passed, or it doesn't need to be audited
                                  (and (contains? #{nil "passed"} audit-state)
                                       ;; AND it has a template id
                                       (:id template)
                                       ;; AND it has defined dimensions
                                       (every? nat-int? template-dimensions)
                                       ;; AND it isn't marked as not selectable
                                       (not (contains? not-selectable (:id template))))))}))))

(defui modal-content [{:keys [step channel visible-ad-types ad-type set-ad-type set-template
                              video-format-step? video-format set-video-format]}]
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {templates :data} (use-query (template.q/templates-query {:advertiser-id advertiser-id}))
        {pre-made-templates :data} (use-query (template.q/pre-made-templates-query))
        {upgrade-hints :data} (use-query (aq/ad-builders-upgrade-hints-query {:adset-id adset-id}
                                                                             {:show-inactive true}))

        relevant-keys (if (= :video (d/get-component-type ad-type))
                        [:width :height :duration]
                        [:width :height])

        ;; "STV" swaps in a different ad-builder-urn, so dimension clashes need
        ;; to be checked against that urn instead of the ad-type's default one.
        effective-urn (or (d/video-format->ad-builder-urn-override video-format)
                          (:ad-builder-urn (d/ad-types ad-type)))

        used-dimensions (uix/use-memo
                         (fn []
                           (->> upgrade-hints
                                (filter #(= (:urn %) effective-urn))
                                (mapcat (fn [{:keys [ad-formats]}]
                                          (mapv #(select-keys (:current-revision %) relevant-keys) ad-formats)))
                                set))
                         [effective-urn upgrade-hints relevant-keys])

        not-selectable (uix/use-memo
                        (fn []
                          (->> (concat templates pre-made-templates)
                               (filter #(contains? used-dimensions
                                                   (select-keys (:latestRevision %) relevant-keys)))
                               (map :id)
                               set))
                        [templates pre-made-templates used-dimensions relevant-keys])]
    ($ Stack
       {:spacing 2
        :sx #js {:minWidth 720
                 :minHeight 300
                 :overflowY "hidden"}}
       (when video-format-step?
         ($ video-format-stepper {:active-step step}))
       (case (step->view step)
         :ad-type ($ form-field
                     {:label "Format"}
                     (if video-format-step?
                       ($ vfp/video-format-selector
                          {:video-format video-format
                           :set-video-format set-video-format})
                       ($ ad-types-selector
                          {:visible-ad-types visible-ad-types
                           :ad-type ad-type
                           :set-ad-type set-ad-type}))
                     ($ hint (if video-format-step?
                               "Choose which platform this ad builder is for."
                               "Choose how you like to structure your ad.")))
         :template ($ template-select
                      {:not-selectable not-selectable
                       :channel channel
                       :select-fn (fn [templates]
                                    (set-template (first templates)))})))))

(defui action-bar
  [{:keys [step set-step ad-type template video-format-step? video-format]}]
  (let [{:keys [adset-id]} (use-subscribe [:url-context])
        {patch-ad-builders :mutate} (use-mutation (q/patch-ad-builders {:adset-id adset-id}))

        final-step? (= step (apply max (keys step->view)))
        urn-override (d/video-format->ad-builder-urn-override video-format)]
    ($ Stack {:direction "row" :spacing 1}
       ($ Button
          {:variant "link"
           :onClick (fn []
                      (if final-step?
                        (set-step dec)
                        (rf/dispatch [:modal/close])))}
          (if final-step? "Back" "Cancel"))
       ($ Button
          {:variant "contained"
           :onClick (fn []
                      (if final-step?
                        (patch-ad-builders (h/->add-ad-builder-payload ad-type template urn-override)
                                           #js {:onSuccess (fn []
                                                             (rf/dispatch [:modal/close]))})
                        (set-step inc)))
           :disabled (or (nil? ad-type)
                         (and (not final-step?) video-format-step? (nil? video-format))
                         (and final-step? (nil? template)))}
          (if final-step? "Create ad builder" "Continue")))))

(defmodal add-template :ad-builder/add-template-modal []
  (let [{:keys [channel] :as adset} (use-adset)
        video? (= channel "video")
        ;; Behind a flag while we roll out the STV format: without it, video
        ;; ad-builders go straight to their one and only ad-type, like before.
        video-format-step? (and video? (use-flag :ad-builder-stv-selection))
        visible-ad-types (d/dynamic-creation-ad-types adset)
        single-ad-type? (<= (count visible-ad-types) 1)
        should-select-ad-formats? (or video-format-step? (not single-ad-type?))

        [step set-step] (uix/use-state (if should-select-ad-formats? 0 1))
        [template set-template] (uix/use-state nil)
        [ad-type set-ad-type] (uix/use-state (when (or video? single-ad-type?)
                                               (-> visible-ad-types keys first)))
        [video-format set-video-format] (uix/use-state nil)]
    ($ modal-v2
       {:title "Create ad builder"
        :footer ($ action-bar
                   {:step step
                    :set-step set-step
                    :ad-type ad-type
                    :template template
                    :video-format-step? video-format-step?
                    :video-format video-format})
        :dialog-content-props {:sx #js {:display "flex"
                                        :overflowY "hidden"}}}
       ($ modal-content
          {:step step
           :channel channel
           :visible-ad-types visible-ad-types
           :ad-type ad-type
           :set-ad-type set-ad-type
           :set-template set-template
           :video-format-step? video-format-step?
           :video-format video-format
           :set-video-format set-video-format}))))

(defmodal change-template :ad-builder/change-template-modal [{:keys [ad-builder]}]
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        channel (use-channel)
        {templates :data} (use-query (template.q/templates-query {:advertiser-id advertiser-id}))
        {pre-made-templates :data} (use-query (template.q/pre-made-templates-query))

        current-revision (get-in ad-builder [:ad-formats 0 :current-revision])
        allowed-dimensions (-> (select-keys current-revision [:width :height :duration])
                               util/filter-nils-map)

        not-selectable (uix/use-memo
                        (fn []
                          (->> (concat templates pre-made-templates)
                               (filter (fn [{:keys [latestRevision]}]
                                         ;; Template is not selectable if it is the current template
                                         ;; OR it does not match the dimensions of the current template.
                                         (or (= (:id current-revision) (:id latestRevision))
                                             (not= allowed-dimensions (select-keys latestRevision (keys allowed-dimensions))))))
                               (map :id)
                               set))
                        [templates pre-made-templates allowed-dimensions current-revision])

        [template set-template] (uix/use-state nil)

        {patch-ad-builders :mutate} (use-mutation (q/patch-ad-builders {:adset-id adset-id}))]
    ($ modal-v2
       {:title "Change template"
        :footer ($ Stack {:direction "row" :spacing 1}
                   ($ Button
                      {:variant "link"
                       :onClick #(rf/dispatch [:modal/close])}
                      "Cancel")
                   ($ Button
                      {:variant "contained"
                       :onClick #(patch-ad-builders (h/->update-ad-builder-template-payload ad-builder template)
                                                    #js {:onSuccess (fn []
                                                                      (rf/dispatch [:modal/close]))})
                       :disabled (nil? template)}
                      "Change template"))}
       ($ Stack
          {:spacing 2
           :sx #js {:minWidth 720
                    :minHeight 300
                    :overflowY "hidden"}}
          ($ template-select
             {:not-selectable not-selectable
              :channel channel
              :select-fn (fn [templates]
                           (set-template (first templates)))})))))

(defmodal deactivation-warning :ad-builder/deactivation-warning
  [{:keys [on-submit]}]
  ($ modal-v2
     {:title "Warning"
      :footer ($ :<>
                 ($ Button
                    {:variant "text"
                     :sx #js {:color "text.primary"}
                     :onClick #(rf/dispatch [:modal/close])}
                    "Cancel")
                 ($ Button
                    {:variant "contained"
                     :onClick (fn []
                                (rf/dispatch [:modal/close])
                                (on-submit))}
                    "Deactivate"))
      :sx #js {:maxWidth 580}}
     ($ :span
        "Deactivating the Ad builder will stop creating new ad variants for this Ad builder.
         All previously created ad variants using this ad builder will still exist and remain unchanged.")))
